/**
 * Payment Card Component
 * Displays payment information in a card format
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Payment } from '../../types/payment';
import { PaymentStatus } from '../../types/payment';
import { formatCurrency, formatDateTime } from '../../utils/formatting';
import { PaymentStatusBadge, PaymentMethodBadge, PaymentPurposeBadge } from './PaymentStatusBadge';

export interface PaymentCardProps {
  payment: Payment;
  onClick?: () => void;
  showDetails?: boolean;
}

export const PaymentCard: React.FC<PaymentCardProps> = ({
  payment,
  onClick,
  showDetails = false,
}) => {
  const { t } = useTranslation();

  const isClickable = Boolean(onClick);

  return (
    <div
      className={`border rounded-lg p-4 ${
        isClickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
      } ${
        payment.status === PaymentStatus.FAILED
          ? 'border-red-300 bg-red-50'
          : payment.status === PaymentStatus.PENDING
          ? 'border-yellow-300 bg-yellow-50'
          : payment.status === PaymentStatus.COMPLETED
          ? 'border-green-300 bg-green-50'
          : 'border-gray-300 bg-white'
      }`}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">
            {formatCurrency(payment.amount, payment.currency)}
          </h3>
          <p className="text-sm text-gray-600">{payment.description}</p>
        </div>
        <PaymentStatusBadge status={payment.status} />
      </div>

      <div className="space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">{t('payments:payment.transactionId')}:</span>
          <span className="font-mono font-medium">{payment.transactionId}</span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-600">{t('payments:payment.purpose')}:</span>
          <PaymentPurposeBadge purpose={payment.purpose} />
        </div>

        {payment.paymentMethod && (
          <div className="flex justify-between">
            <span className="text-gray-600">{t('payments:payment.method')}:</span>
            <PaymentMethodBadge method={payment.paymentMethod} />
          </div>
        )}

        {payment.paymentDate && (
          <div className="flex justify-between">
            <span className="text-gray-600">{t('payments:payment.paymentDate')}:</span>
            <span>{formatDateTime(payment.paymentDate)}</span>
          </div>
        )}

        {payment.dueDate && !payment.paymentDate && (
          <div className="flex justify-between">
            <span className="text-gray-600">{t('payments:payment.dueDate')}:</span>
            <span
              className={
                new Date(payment.dueDate) < new Date() &&
                payment.status === PaymentStatus.PENDING
                  ? 'text-red-600 font-medium'
                  : ''
              }
            >
              {formatDateTime(payment.dueDate)}
            </span>
          </div>
        )}
      </div>

      {showDetails && (
        <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">{t('payments:payment.from')}:</span>
            <span className="text-right">{payment.payer.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{t('payments:payment.to')}:</span>
            <span className="text-right">{payment.payee.name}</span>
          </div>
          {payment.receiptNumber && (
            <div className="flex justify-between">
              <span className="text-gray-600">{t('payments:payment.receiptNumber')}:</span>
              <span className="font-mono">{payment.receiptNumber}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
